/**
 * useAuth0Session - Unified Auth0 Session Management Hook
 *
 * Consolidates token refresh, activity tracking, and timeout dialog
 * into a single hook with proper cross-tab coordination.
 *
 * Features:
 * - Web Locks API for leader election (AbortController-based, no timers)
 * - Activity tracking with localStorage as source of truth
 * - Token refresh with exponential backoff
 * - Terminal error detection (no retry on invalid_grant, etc.)
 * - Dialog state using absolute timestamps for accurate countdown
 * - Cross-tab synchronization via BroadcastChannel
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { jwtDecode } from 'jwt-decode';
import { createBackoffController } from '../utils/backoff';
import { setTokenRefreshHandlers } from '../utils/bbAxios';
import {
  setToken as setSocketToken,
  initSocketManager,
  disconnectAll as disconnectAllSockets,
} from '../utils/socketManager';
import { subscribe, broadcast, closeAllChannels } from '../utils/sessionChannelManager';
import {
  getTabId,
  resetTabId,
  readTimestamp,
  writeTimestamp,
  clearStorageByPattern,
} from '../utils/storageUtils';
import { getTokenExpiration } from '../utils/tokenUtils';
import { createLeaderElection } from '../utils/leaderElection';
import { createActivityManager } from '../utils/activityManager';
import {
  MIN_REFRESH_INTERVAL_MS,
  DIALOG_CHECK_INTERVAL_MS,
  MAX_RETRIES,
  STORAGE_KEYS,
  CHANNEL_NAMES,
  MESSAGE_TYPES,
  LOCK_NAMES,
  AUTH0_REFRESH_TIMEOUT_MS,
  WARNING_THRESHOLD_MS,
  AUTH0_SDK_LEEWAY_MS,
  AUTH0_REFRESH_BUFFER_MS,
  AUTH0_BACKOFF_DELAY_MS,
  JUST_CONTINUED_GRACE_MS,
  FOLLOWER_WAIT_TIMEOUT_MS,
  STORAGE_KEY_DEBOUNCE_MS,
  STORAGE_KEY_DEBOUNCE_REFRESHING_MS,
  EXPIRED_SAFETY_NET_MS,
  isTerminalError,
} from '../sessionConfig';

// ============================================================================
// MODULE-LEVEL SINGLETONS
// ============================================================================

// Leader election controller
let leaderController = null;

// Activity manager instance
let activityManager = null;

/**
 * Get or create leader controller singleton
 */
const getLeaderController = () => {
  if (!leaderController) {
    leaderController = createLeaderElection({
      lockName: LOCK_NAMES.AUTH0,
      logPrefix: '[Auth0Session]',
    });
  }
  return leaderController;
};

/**
 * Get or create activity manager singleton
 */
const getActivityManager = () => {
  if (!activityManager) {
    activityManager = createActivityManager({
      activityKey: STORAGE_KEYS.ACTIVITY,
      refreshKey: STORAGE_KEYS.REFRESH,
      channelName: CHANNEL_NAMES.ACTIVITY,
      logPrefix: '[Auth0Session]',
    });
  }
  return activityManager;
};

/**
 * Clear Auth0-related localStorage keys
 */
const clearAuth0Storage = () => {
  try {
    Object.values(STORAGE_KEYS).forEach((key) => {
      localStorage.removeItem(key);
    });
    clearStorageByPattern('@@auth0spajs@@');
  } catch (error) {
    console.error('[Auth0Session] Failed to clear storage:', error);
  }
};

/**
 * Reset all module state (called on logout)
 */
const resetModuleState = () => {
  resetTabId();
  getActivityManager().reset();
  getLeaderController().reset();
};

// ============================================================================
// MAIN HOOK
// ============================================================================

/**
 * Unified Auth0 Session Hook
 *
 * @param {Object} options
 * @param {string} options.loginRoute - Route to redirect on logout
 * @param {Function} options.onSessionExpired - Called when session expires
 * @returns {Object} Session state and controls
 */
export const useAuth0Session = ({ loginRoute = '/login', onSessionExpired }) => {
  // Stabilize onSessionExpired via ref to prevent dependency cascade.
  // This callback is often passed as an inline function, and countdown state
  // changes every second cause re-renders that create new function identities,
  // which would cascade through performRefresh → scheduleRefresh → the main effect.
  const onSessionExpiredRef = useRef(onSessionExpired);
  useEffect(() => {
    onSessionExpiredRef.current = onSessionExpired;
  });
  const auth0 = useAuth0();
  const leader = getLeaderController();
  const activity = getActivityManager();

  // React state for UI
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [dialogExpirationTimestamp, setDialogExpirationTimestamp] = useState(null);
  const [countdown, setCountdown] = useState(0);

  // Refs for stable callbacks
  const auth0Ref = useRef(auth0);
  const isDialogOpenRef = useRef(false);
  const backoffRef = useRef(createBackoffController({ maxRetries: MAX_RETRIES }));
  const lastRefreshAttemptRef = useRef(0);
  const refreshTimeoutRef = useRef(null);
  const dialogCheckIntervalRef = useRef(null);
  const countdownIntervalRef = useRef(null);
  const isRefreshingRef = useRef(false);
  // Shared promise for in-flight refresh — allows concurrent callers to await the same
  // refresh instead of racing or returning null. Prevents React StrictMode double-invocation
  // and safety-net/scheduler overlap from triggering parallel SDK refresh exchanges.
  const refreshPromiseRef = useRef(null);
  // Track when user explicitly continued (timestamp-based, NOT timeout-based for reliability)
  // If Date.now() - justContinuedAtRef.current < JUST_CONTINUED_GRACE_MS, skip dialog
  const justContinuedAtRef = useRef(0);
  // Debounce timer for @@auth0spajs@@ storage key removal detection
  const storageKeyRemovedTimeoutRef = useRef(null);
  // Track last token signature to detect if Auth0 actually refreshed
  const lastTokenSigRef = useRef(null);
  // Track last token expiration to detect if token actually changed (Auth0-specific)
  const lastTokenExpMsRef = useRef(null);
  // Track current token's IAT for activity comparison (Auth0-specific)
  const currentTokenIatMsRef = useRef(null);
  // CRITICAL: Track when dialog countdown is actively running to BLOCK all token operations.
  // When true, no refresh attempts should occur - user must respond to dialog or countdown completes.
  // This prevents Auth0 SDK from triggering iframe redirects/page refreshes during countdown.
  const dialogCountdownActiveRef = useRef(false);
  // Track the expiration timestamp for the active countdown (used for safety checks)
  const countdownExpirationRef = useRef(null);

  // Keep refs updated
  useEffect(() => {
    auth0Ref.current = auth0;
  }, [auth0]);

  useEffect(() => {
    isDialogOpenRef.current = isDialogOpen;
  }, [isDialogOpen]);

  // ============================================================================
  // LOGOUT HANDLER
  // ============================================================================

  const handleLogout = useCallback(
    (reason = 'user', { skipBroadcast = false } = {}) => {
      console.log(`[Auth0Session] 🚪 Logging out (reason: ${reason})`);

      disconnectAllSockets();
      clearAuth0Storage();

      // Broadcast BEFORE closing channels so we use the existing channel instance.
      // skipBroadcast=true when this logout was triggered by another tab's broadcast,
      // preventing a broadcast storm across many tabs.
      if (!skipBroadcast) {
        broadcast(CHANNEL_NAMES.SESSION, {
          type: MESSAGE_TYPES.LOGOUT_REQUESTED,
          reason,
        });
      }

      closeAllChannels();
      resetModuleState();

      if (refreshTimeoutRef.current) clearTimeout(refreshTimeoutRef.current);
      if (dialogCheckIntervalRef.current) clearInterval(dialogCheckIntervalRef.current);
      if (countdownIntervalRef.current) clearInterval(countdownIntervalRef.current);

      // Include current path as redirect so login sends the user back.
      // Strip any existing 'redirect' param to prevent nested redirect chains.
      const url = new URL(window.location.href);
      url.searchParams.delete('redirect');
      const returnUrl = url.pathname + url.search;
      const separator = loginRoute.includes('?') ? '&' : '?';
      const returnToUrl = `${window.location.origin}${loginRoute}${separator}redirect=${encodeURIComponent(returnUrl)}`;

      // Null-guard: auth0Ref.current may be null if component unmounted during countdown.
      // Fallback to hard redirect (mirrors OIDC's unfailable pattern).
      if (auth0Ref.current?.logout) {
        auth0Ref.current.logout({
          logoutParams: {
            returnTo: returnToUrl,
          },
        });
      } else {
        window.location.href = `${loginRoute}${separator}redirect=${encodeURIComponent(returnUrl)}`;
      }
    },
    [loginRoute]
  );

  // ============================================================================
  // TOKEN REFRESH
  // ============================================================================

  const performRefresh = useCallback(async () => {
    // ── Deduplication ─────────────────────────────────────────────────
    // If a refresh is already in flight, share its promise so concurrent
    // callers (React StrictMode, safety-net overlap, auth0Handler) all
    // receive the same result instead of racing or returning null.
    if (refreshPromiseRef.current) {
      return refreshPromiseRef.current;
    }

    const currentAuth0 = auth0Ref.current;

    // Pre-flight checks with logging for debugging
    if (!currentAuth0?.isAuthenticated) {
      console.log('[Auth0Session] ⏸️ Skipping refresh - not authenticated');
      return null;
    }
    // CRITICAL: Block ALL refresh attempts when countdown is active.
    // This prevents Auth0 SDK from making iframe requests that could trigger page redirects.
    // The user MUST either click Continue (which dismisses dialog and allows refresh) or
    // let the countdown reach 0 (which triggers logout).
    if (dialogCountdownActiveRef.current) {
      console.log(
        '[Auth0Session] ⏸️ Skipping refresh - countdown active, user must respond to dialog'
      );
      return null;
    }
    if (isDialogOpenRef.current) {
      console.log('[Auth0Session] ⏸️ Skipping refresh - dialog is open');
      return null;
    }
    if (!leader.isLeader()) {
      return null; // Normal for followers, no log needed
    }
    if (!navigator.onLine) {
      console.log('[Auth0Session] ⏸️ Skipping refresh - offline');
      return null;
    }

    // Auth0-specific activity check using token's IAT as source of truth
    // This eliminates cross-tab race conditions from using local Date.now()
    const currentIatMs = currentTokenIatMsRef.current;
    const lastActivity = activity.getLastActivity();
    const isActive = currentIatMs
      ? activity.isUserActiveForToken(currentIatMs)
      : activity.isUserActive();

    if (!isActive) {
      console.log('[Auth0Session] ⏸️ Skipping refresh - user not active', {
        lastActivity,
        tokenIatMs: currentIatMs,
        timeSinceActivity: lastActivity
          ? Math.round((Date.now() - lastActivity) / 1000) + 's'
          : 'never',
        timeSinceIat: currentIatMs
          ? Math.round((Date.now() - currentIatMs) / 1000) + 's'
          : 'unknown',
      });
      return null;
    }

    const now = Date.now();
    if (now - lastRefreshAttemptRef.current < MIN_REFRESH_INTERVAL_MS) {
      return null; // Rate limited, no log needed
    }
    lastRefreshAttemptRef.current = now;

    // ── Tracked refresh promise ──────────────────────────────────────
    // Wrap the actual SDK call in an IIFE so concurrent arrivals that
    // pass pre-flight (or arrive after pre-flight but before the await)
    // share the same network round-trip via refreshPromiseRef.
    const refreshWork = (async () => {
      isRefreshingRef.current = true;
      console.log('[Auth0Session] 🔄 performRefresh started', {
        isLeader: leader.isLeader(),
        previousExpSec: lastTokenExpMsRef.current
          ? Math.round((lastTokenExpMsRef.current - Date.now()) / 1000) + 's'
          : 'none',
      });

      // Capture previous expiration for comparison
      const previousExpMs = lastTokenExpMsRef.current;

      try {
        // Add timeout safety valve to prevent hanging forever
        const newToken = await Promise.race([
          currentAuth0.getAccessTokenSilently({ cacheMode: 'off' }),
          new Promise((_, reject) =>
            setTimeout(
              () => reject(new Error('Auth0 refresh timeout')),
              AUTH0_REFRESH_TIMEOUT_MS
            )
          ),
        ]);

        // Defensive: cacheMode: 'off' should always return a token or throw,
        // but guard against SDK behavioral changes.
        if (!newToken) {
          throw new Error('getAccessTokenSilently returned undefined for cacheMode: off');
        }

        const decoded = jwtDecode(newToken);
        const newExpiration = decoded?.exp ? decoded.exp * 1000 : null;
        const newIatMs = decoded?.iat ? decoded.iat * 1000 : null;

        // Auth0 token comparison: check if we got a NEW token or the same one
        // Auth0 SDK only actually refreshes when token is close to expiry (~60s)
        const tokenSig = newToken.slice(-20); // Last 20 chars as signature
        const tokenChanged =
          lastTokenSigRef.current !== tokenSig &&
          (previousExpMs === null || newExpiration !== previousExpMs);

        console.log(
          `[Auth0Session] ✅ Refreshed, expires in ${Math.round((newExpiration - Date.now()) / 1000)}s`,
          {
            tokenChanged,
            previousExpSec: previousExpMs
              ? Math.round((previousExpMs - Date.now()) / 1000)
              : 'none',
            newExpSec: newExpiration
              ? Math.round((newExpiration - Date.now()) / 1000)
              : 'unknown',
            iat: decoded.iat ? new Date(decoded.iat * 1000).toISOString() : 'unknown',
          }
        );

        // Update tracking refs
        lastTokenSigRef.current = tokenSig;
        lastTokenExpMsRef.current = newExpiration;
        currentTokenIatMsRef.current = newIatMs;

        backoffRef.current.reset();

        // Update refresh timestamp only — activity must come from real user interaction
        if (tokenChanged && newIatMs) {
          activity.updateRefreshTimestamp(newIatMs);
        }

        // Broadcast to other tabs with token IAT for consistent activity tracking
        broadcast(CHANNEL_NAMES.SESSION, {
          type: MESSAGE_TYPES.TOKEN_REFRESHED,
          expiration: newExpiration,
          tabId: getTabId(),
          // Include token's IAT so other tabs can sync activity correctly
          tokenIatMs: newIatMs,
          tokenChanged,
        });

        // Update socket manager auth for future reconnections
        setSocketToken(newToken);

        return {
          token: newToken,
          expiration: newExpiration,
          tokenChanged,
          previousExpMs,
          iatMs: newIatMs,
        };
      } catch (error) {
        console.log(`[Auth0Session] ❌ Refresh failed: ${error.message}`);

        if (isTerminalError(error)) {
          handleLogout('terminal_error');
          // Re-throw so callers (e.g., bbAxios) can detect the terminal error
          // and trip the circuit breaker to prevent further refresh attempts
          throw error;
        }

        if (backoffRef.current.canRetry()) {
          const delay = backoffRef.current.nextDelay();
          refreshTimeoutRef.current = setTimeout(performRefresh, delay);
        } else {
          backoffRef.current.reset();
          onSessionExpiredRef.current?.();
        }

        return null;
      } finally {
        isRefreshingRef.current = false;
        console.log('[Auth0Session] 🔄 performRefresh finished (isRefreshing=false)');
      }
    })(); // end refreshWork IIFE

    refreshPromiseRef.current = refreshWork;
    try {
      return await refreshWork;
    } finally {
      refreshPromiseRef.current = null;
    }
  }, [handleLogout, activity, leader]);

  const scheduleRefresh = useCallback(async () => {
    if (!leader.isLeader()) return;
    if (!auth0Ref.current?.isAuthenticated) {
      console.log('[Auth0Session] ⏰ Skipping schedule - not authenticated');
      return;
    }
    // CRITICAL: Do not schedule refresh when countdown is active - let countdown complete
    if (dialogCountdownActiveRef.current) {
      console.log('[Auth0Session] ⏰ Skipping schedule - countdown active');
      return;
    }

    let token;
    let decoded;
    try {
      // IMPORTANT: Use cache-only to just READ the token without triggering SDK's internal refresh.
      // Without this, getAccessTokenSilently auto-refreshes when token has ≤60s remaining,
      // bypassing our dialog check in performRefresh.
      token = await auth0Ref.current.getAccessTokenSilently({
        cacheMode: 'cache-only',
      });

      // v2 SDK: cache-only returns undefined (not throw) when token is missing/expired.
      // Treat as cache miss — fall through to catch-block handling below.
      if (!token) {
        throw new Error('Cache miss: cache-only returned undefined');
      }

      decoded = jwtDecode(token);
      if (!decoded?.exp) {
        console.warn('[Auth0Session] Token has no exp claim');
        return;
      }
    } catch (cacheError) {
      // cache-only can throw if token is expired or missing from cache
      console.warn('[Auth0Session] cache-only token read failed:', cacheError.message, {
        isLeader: leader.isLeader(),
        isRefreshing: isRefreshingRef.current,
        hasScheduledRefresh: !!refreshTimeoutRef.current,
        dialogOpen: isDialogOpenRef.current,
        countdownActive: dialogCountdownActiveRef.current,
        lastExpMs: lastTokenExpMsRef.current
          ? Math.round((lastTokenExpMsRef.current - Date.now()) / 1000) + 's'
          : 'none',
        lastRefreshAttempt: lastRefreshAttemptRef.current
          ? Math.round((Date.now() - lastRefreshAttemptRef.current) / 1000) + 's ago'
          : 'never',
      });

      // If countdown is active, don't interfere - let it complete
      if (dialogCountdownActiveRef.current) {
        console.log(
          '[Auth0Session] Token unavailable but countdown active - letting countdown handle it'
        );
        return;
      }

      // If dialog is open (but countdown not active yet), don't interfere
      if (isDialogOpenRef.current) {
        console.log(
          '[Auth0Session] Token unavailable but dialog open - waiting for user response'
        );
        return;
      }

      // If we're currently refreshing, wait for that to complete
      if (isRefreshingRef.current) {
        console.log('[Auth0Session] Token unavailable but refresh in progress - waiting');
        refreshTimeoutRef.current = setTimeout(scheduleRefresh, MIN_REFRESH_INTERVAL_MS);
        return;
      }

      // Token is unexpectedly missing and we're not in any special state
      // This could be a terminal state - check if we should logout
      if (isTerminalError(cacheError)) {
        console.log('[Auth0Session] Terminal error reading token - logging out');
        handleLogout('token_cache_terminal_error');
        return;
      }

      // Cache-only failed — likely because the SDK considers the token expired
      // (within its 60s leeway window). Instead of retrying scheduleRefresh (which
      // will fail again with cache-only), call performRefresh directly to get a
      // new token. performRefresh uses cacheMode: 'off' which bypasses the cache.
      if (leader.isLeader()) {
        console.log('[Auth0Session] Cache-only failed, attempting direct refresh', {
          auth0KeysInStorage: Object.keys(localStorage).filter((k) =>
            k.includes('@@auth0spajs@@')
          ).length,
        });
        try {
          const result = await performRefresh();
          if (result?.tokenChanged) {
            scheduleRefresh();
          } else if (result) {
            // Same token returned — SDK wasn't ready to refresh yet, backoff
            refreshTimeoutRef.current = setTimeout(
              scheduleRefresh,
              AUTH0_BACKOFF_DELAY_MS
            );
          } else {
            // null — pre-flight check failed (inactive, offline, etc.) or error
            refreshTimeoutRef.current = setTimeout(
              scheduleRefresh,
              MIN_REFRESH_INTERVAL_MS
            );
          }
        } catch (e) {
          // Terminal error — handleLogout already called inside performRefresh
          return;
        }
      } else {
        console.log('[Auth0Session] Retrying schedule after cache-only failure');
        refreshTimeoutRef.current = setTimeout(scheduleRefresh, AUTH0_BACKOFF_DELAY_MS);
      }
      return;
    }

    // Token successfully read from cache - continue with scheduling
    const expiresAtMs = decoded.exp * 1000;
    const iatMs = decoded.iat ? decoded.iat * 1000 : null;
    const now = Date.now();

    // Initialize socket manager and set initial token
    initSocketManager();
    setSocketToken(token);

    // Update tracking refs for activity comparison
    lastTokenExpMsRef.current = expiresAtMs;
    if (iatMs) {
      currentTokenIatMsRef.current = iatMs;
    }

    // Auth0-specific: Target refresh at 55s before expiry (inside SDK's 60s window)
    // This ensures the SDK will actually refresh the token instead of returning cached one.
    // Formula: refreshTargetMs = AUTH0_SDK_LEEWAY_MS - AUTH0_REFRESH_BUFFER_MS = 60s - 5s = 55s
    const refreshTargetMs = AUTH0_SDK_LEEWAY_MS - AUTH0_REFRESH_BUFFER_MS;
    const targetRefreshTime = expiresAtMs - refreshTargetMs;
    const delayMs = targetRefreshTime - now;

    if (refreshTimeoutRef.current) {
      clearTimeout(refreshTimeoutRef.current);
      refreshTimeoutRef.current = null;
    }

    if (delayMs <= 0) {
      // Already inside the SDK's refresh window (≤55s remaining)
      // Attempt refresh if we haven't recently
      if (now - lastRefreshAttemptRef.current >= MIN_REFRESH_INTERVAL_MS) {
        console.log(
          `[Auth0Session] ⏰ Refresh due now (${Math.round((expiresAtMs - now) / 1000)}s remaining)`
        );
        try {
          const result = await performRefresh();

          if (result?.tokenChanged) {
            // Got a new token - reschedule for the new token's expiry
            console.log('[Auth0Session] ⏰ Token refreshed successfully, rescheduling');
            scheduleRefresh();
          } else if (result && !result.tokenChanged) {
            // Auth0 returned the same token (still outside true refresh window)
            // Apply backoff instead of infinite loop
            console.log(
              `[Auth0Session] ⏰ Auth0 returned same token, backing off ${AUTH0_BACKOFF_DELAY_MS / 1000}s`
            );
            refreshTimeoutRef.current = setTimeout(
              scheduleRefresh,
              AUTH0_BACKOFF_DELAY_MS
            );
          } else {
            // result is null (pre-flight check failed or error)
            // Schedule retry with shorter backoff
            console.log('[Auth0Session] ⏰ Refresh skipped/failed, retrying in 5s');
            refreshTimeoutRef.current = setTimeout(
              scheduleRefresh,
              MIN_REFRESH_INTERVAL_MS
            );
          }
        } catch (e) {
          // Terminal error — handleLogout already called inside performRefresh
          return;
        }
      } else {
        // Rate limited - wait a bit and retry
        const retryIn = MIN_REFRESH_INTERVAL_MS - (now - lastRefreshAttemptRef.current);
        console.log(
          `[Auth0Session] ⏰ Rate limited, retrying in ${Math.round(retryIn / 1000)}s`
        );
        refreshTimeoutRef.current = setTimeout(scheduleRefresh, retryIn);
      }
    } else {
      // Token has plenty of time left - schedule for when we enter SDK's refresh window
      console.log(
        `[Auth0Session] ⏰ Refresh scheduled in ${Math.round(delayMs / 1000)}s (at T-${Math.round(refreshTargetMs / 1000)}s)`
      );
      refreshTimeoutRef.current = setTimeout(scheduleRefresh, delayMs);
    }
  }, [performRefresh, leader, handleLogout]);

  // ============================================================================
  // DIALOG MANAGEMENT
  // ============================================================================

  const showDialog = useCallback(
    (expiration) => {
      if (isDialogOpenRef.current) return;

      console.log(
        `[Auth0Session] ⚠️ Dialog opening, ${Math.round((expiration - Date.now()) / 1000)}s until timeout`
      );

      // Update ref synchronously BEFORE setIsDialogOpen to prevent race:
      // BroadcastChannel DIALOG_SHOWN from another tab can arrive before
      // the useEffect that syncs the ref, causing showDialog to run twice.
      isDialogOpenRef.current = true;
      setIsDialogOpen(true);
      setDialogExpirationTimestamp(expiration);
      writeTimestamp(STORAGE_KEYS.DIALOG_OPEN, Date.now());

      // CRITICAL: Mark countdown as active to block ALL token operations
      // This prevents Auth0 SDK iframe requests that could cause page redirects
      dialogCountdownActiveRef.current = true;
      countdownExpirationRef.current = expiration;

      broadcast(CHANNEL_NAMES.SESSION, {
        type: MESSAGE_TYPES.DIALOG_SHOWN,
        expiration,
        tabId: getTabId(),
      });

      // Clear any pending refresh timeouts - countdown takes priority
      if (refreshTimeoutRef.current) {
        clearTimeout(refreshTimeoutRef.current);
        refreshTimeoutRef.current = null;
      }

      if (countdownIntervalRef.current) {
        clearInterval(countdownIntervalRef.current);
      }

      const updateCountdown = () => {
        const remaining = Math.max(0, Math.ceil((expiration - Date.now()) / 1000));
        setCountdown(remaining);

        // SAFETY: Double-check expiration timestamp hasn't drifted
        // This guards against edge cases where countdown state could be corrupted
        const expectedExpiration = countdownExpirationRef.current;
        const actualRemaining = expectedExpiration
          ? Math.ceil((expectedExpiration - Date.now()) / 1000)
          : remaining;

        if (remaining <= 0 || actualRemaining <= 0) {
          console.log('[Auth0Session] ⏱️ Countdown reached zero - triggering logout', {
            remaining,
            actualRemaining,
            dialogCountdownActive: dialogCountdownActiveRef.current,
          });
          clearInterval(countdownIntervalRef.current);
          countdownIntervalRef.current = null;
          dialogCountdownActiveRef.current = false;
          countdownExpirationRef.current = null;
          handleLogout('timeout');
        }
      };

      updateCountdown();
      countdownIntervalRef.current = setInterval(updateCountdown, 1000);
    },
    [handleLogout]
  );

  const dismissDialog = useCallback(() => {
    console.log('[Auth0Session] 🔕 Dismissing dialog', {
      wasCountdownActive: dialogCountdownActiveRef.current,
    });

    isDialogOpenRef.current = false; // Sync ref immediately (mirrors showDialog pattern)
    setIsDialogOpen(false);
    setDialogExpirationTimestamp(null);
    setCountdown(0);

    // CRITICAL: Clear countdown active flag to allow token operations again
    dialogCountdownActiveRef.current = false;
    countdownExpirationRef.current = null;

    try {
      localStorage.removeItem(STORAGE_KEYS.DIALOG_OPEN);
    } catch (e) {
      // Ignore
    }

    if (countdownIntervalRef.current) {
      clearInterval(countdownIntervalRef.current);
      countdownIntervalRef.current = null;
    }

    broadcast(CHANNEL_NAMES.SESSION, {
      type: MESSAGE_TYPES.DIALOG_DISMISSED,
      tabId: getTabId(),
    });
  }, []);

  const handleContinue = useCallback(async () => {
    const currentAuth0 = auth0Ref.current;
    if (!currentAuth0?.isAuthenticated) {
      dismissDialog();
      return;
    }

    // Auth0-specific: Don't force immediate getAccessTokenSilently call.
    // The SDK won't refresh tokens with >60s remaining anyway.
    // Instead, update activity timestamp to mark user as active,
    // close the dialog, and let the scheduler handle refresh at T-55s.

    const tokenExpMs = lastTokenExpMsRef.current;
    const tokenRemaining = tokenExpMs
      ? Math.round((tokenExpMs - Date.now()) / 1000)
      : null;
    console.log('[Auth0Session] 🔄 User clicked Continue - updating activity', {
      tokenRemaining: tokenRemaining !== null ? tokenRemaining + 's' : 'unknown',
      isLeader: leader.isLeader(),
      inRefreshWindow: tokenRemaining !== null && tokenRemaining <= 55,
      isRefreshing: isRefreshingRef.current,
      hasScheduledRefresh: !!refreshTimeoutRef.current,
    });

    // Record when user continued (timestamp-based, NOT timeout-based for reliability)
    // This prevents immediate dialog reopen during the grace period
    const continueTime = Date.now();
    justContinuedAtRef.current = continueTime;

    // Update activity timestamp to current time - this marks user as active
    // Use the current token's IAT as the refresh timestamp base for consistency
    const currentIatMs = currentTokenIatMsRef.current || Date.now();
    activity.updateRefreshTimestamp(currentIatMs);
    writeTimestamp(STORAGE_KEYS.ACTIVITY, continueTime); // Activity = now (user just clicked)

    // Also update last refresh attempt to prevent immediate performRefresh
    lastRefreshAttemptRef.current = continueTime;

    // Broadcast to other tabs that user continued (with timestamp for guard sync)
    broadcast(CHANNEL_NAMES.SESSION, {
      type: MESSAGE_TYPES.SESSION_RESET,
      tabId: getTabId(),
      source: 'dialog',
      activityTs: continueTime,
      continueTime, // For timestamp-based guard sync
    });

    // Close the dialog immediately - user perceives they're safe
    dismissDialog();

    // Schedule refresh - will actually refresh when we enter SDK's 55s window
    // If we're already in the window (e.g., user clicked at T-50s), it will refresh now
    scheduleRefresh();
  }, [dismissDialog, scheduleRefresh, activity]);

  const checkShouldShowDialog = useCallback(() => {
    // Use tracked expiration if available (faster than localStorage cross-tab sync)
    const expiration = lastTokenExpMsRef.current || getTokenExpiration();

    // Handle missing token expiration
    if (!expiration) {
      // If countdown is active, check that the interval is actually alive.
      // The React effect cleanup can kill countdownIntervalRef when dependencies
      // change (e.g., leader election state), leaving the dialog frozen.
      if (dialogCountdownActiveRef.current) {
        if (!countdownIntervalRef.current) {
          console.log('[Auth0Session] Dialog countdown interval lost, forcing logout');
          handleLogout('timeout');
          return;
        }
        console.log(
          '[Auth0Session] Token expiration unavailable but countdown active - letting countdown handle it'
        );
        return;
      }

      // If dialog is open but countdown inactive, still waiting for user
      if (isDialogOpenRef.current) {
        console.log(
          '[Auth0Session] Token expiration unavailable but dialog open - waiting for user'
        );
        return;
      }

      // If refresh is in progress, token might become available soon
      if (isRefreshingRef.current) {
        console.log(
          '[Auth0Session] Token expiration unavailable but refresh in progress - waiting'
        );
        return;
      }

      // If we just continued (within grace period), SDK might still be processing
      const sinceLastContinue = Date.now() - justContinuedAtRef.current;
      if (sinceLastContinue < JUST_CONTINUED_GRACE_MS) {
        console.log(
          '[Auth0Session] Token expiration unavailable but within continue grace period - waiting'
        );
        return;
      }

      // Token is genuinely missing/expired and we're not in any recovery state
      // This is likely a terminal state - the session has ended
      console.warn(
        '[Auth0Session] Token expiration unavailable - session may have ended, triggering logout'
      );
      handleLogout('token_unavailable');
      return;
    }

    const now = Date.now();
    const remaining = expiration - now;
    const isWithinWarningWindow = remaining <= WARNING_THRESHOLD_MS && remaining > 0;

    // Auth0-specific: Use token's IAT as source of truth for activity comparison.
    // This eliminates cross-tab race conditions from using local Date.now() for refresh timestamp.
    const currentIatMs = currentTokenIatMsRef.current;
    const isActive = currentIatMs
      ? activity.isUserActiveForToken(currentIatMs)
      : activity.isUserActive(); // Fallback if no IAT tracked yet

    const lastActivity = activity.getLastActivity();

    // Time remaining log every 10s - include IAT for debugging
    console.log(`[Auth0Session] ${Math.round(remaining / 1000)}s | active:${isActive}`, {
      activityTs: lastActivity,
      tokenIatMs: currentIatMs,
      activityAfterIat: currentIatMs ? lastActivity > currentIatMs : 'n/a',
    });

    // TOKEN EXPIRED (remaining <= 0) — handle before warning window / safety-net logic.
    // Without this, inactive users with an expired token fall through to isWithinWarningWindow
    // which is false (requires remaining > 0), resulting in no dialog and no logout.
    if (remaining <= 0) {
      // Dialog countdown active — let the countdown handle logout
      if (dialogCountdownActiveRef.current) {
        if (!countdownIntervalRef.current) {
          console.log('[Auth0Session] Dialog countdown interval lost, forcing logout');
          handleLogout('timeout');
        }
        return;
      }
      // Dialog open but countdown not started yet — waiting for user
      if (isDialogOpenRef.current) return;

      // Active user: recovery via scheduler
      if (isActive) {
        // Safety net: if token has been expired for an unreasonable duration,
        // our activity state is likely stale (e.g., missed TOKEN_REFRESHED broadcast
        // left us with an old tokenIatMs). Force logout to prevent infinite loops
        // where the follower keeps requesting refresh from a leader that's counting down.
        if (remaining < -EXPIRED_SAFETY_NET_MS) {
          console.log(
            '[Auth0Session] Token deeply expired despite appearing active — forcing logout',
            { remaining: Math.round(remaining / 1000) + 's' }
          );
          handleLogout('timeout');
          return;
        }

        // Follower: try to resync token refs from Auth0 SDK cache.
        // If the leader refreshed but we missed the BroadcastChannel message,
        // the Auth0 SDK's localStorage cache will have the new token.
        // This async read updates refs for the next checkShouldShowDialog cycle.
        if (!leader.isLeader() && auth0Ref.current?.isAuthenticated) {
          auth0Ref.current
            .getAccessTokenSilently({ cacheMode: 'cache-only' })
            .then((token) => {
              if (!token) return; // v2: cache-only returns undefined on miss
              try {
                const decoded = jwtDecode(token);
                if (decoded?.exp) {
                  const newExpMs = decoded.exp * 1000;
                  if (newExpMs > (lastTokenExpMsRef.current || 0)) {
                    lastTokenExpMsRef.current = newExpMs;
                    if (decoded?.iat) {
                      currentTokenIatMsRef.current = decoded.iat * 1000;
                    }
                    console.log('[Auth0Session] 📦 Follower resynced token from cache', {
                      newExpSec: Math.round((newExpMs - Date.now()) / 1000),
                    });
                  }
                }
              } catch {
                /* ignore decode errors */
              }
            })
            .catch(() => {
              /* cache miss — token is genuinely expired */
            });
        }

        if (leader.isLeader() && !refreshTimeoutRef.current) {
          scheduleRefresh();
        } else if (!leader.isLeader()) {
          broadcast(CHANNEL_NAMES.SESSION, {
            type: MESSAGE_TYPES.REFRESH_REQUESTED,
            tabId: getTabId(),
          });
        }
        return;
      }
      // Inactive + expired → logout
      console.log('[Auth0Session] Token expired while user inactive — logging out');
      handleLogout('timeout');
      return;
    }

    // SAFETY NET: Ensure refresh is scheduled when we're in the SDK's refresh window.
    // Long setTimeout calls can be throttled in background tabs, so this 10s interval
    // acts as a backup to catch missed refreshes.
    // BUT: Skip if dialog is open OR countdown is active - user must respond to dialog first.
    const refreshTargetMs = AUTH0_SDK_LEEWAY_MS - AUTH0_REFRESH_BUFFER_MS; // 55s
    if (
      remaining <= refreshTargetMs &&
      remaining > 0 &&
      !isDialogOpenRef.current &&
      !dialogCountdownActiveRef.current
    ) {
      if (leader.isLeader()) {
        if (!refreshTimeoutRef.current) {
          console.log('[Auth0Session] ⚠️ Safety net: no pending refresh, rescheduling');
          scheduleRefresh();
        }
      } else if (isActive) {
        // Follower tab with an active user: ask the leader to refresh.
        // Without this, an active follower silently watches the token expire
        // because only the leader runs scheduleRefresh/performRefresh.
        console.log('[Auth0Session] 📡 Follower requesting leader refresh (user active)');
        broadcast(CHANNEL_NAMES.SESSION, {
          type: MESSAGE_TYPES.REFRESH_REQUESTED,
          tabId: getTabId(),
        });
      }
    }

    if (isWithinWarningWindow) {
      // Skip if countdown is already active - dialog is shown and user must respond
      if (dialogCountdownActiveRef.current) {
        return;
      }

      // Timestamp-based guard for race condition right after Continue click
      // More reliable than setTimeout which can be lost due to tab throttling
      const sinceLastContinue = now - justContinuedAtRef.current;
      if (sinceLastContinue < JUST_CONTINUED_GRACE_MS) {
        console.log('[Auth0Session] Skipping dialog - user just continued', {
          sinceLastContinue: Math.round(sinceLastContinue / 1000) + 's ago',
          graceRemaining:
            Math.round((JUST_CONTINUED_GRACE_MS - sinceLastContinue) / 1000) + 's',
        });
        return;
      }

      const shouldOpen = !isActive;
      console.log('[Auth0Session] Dialog decision', {
        shouldOpen,
        isActive,
        remaining: Math.round(remaining / 1000),
        lastActivity: lastActivity
          ? Math.round((now - lastActivity) / 1000) + 's ago'
          : 'never',
        tokenIatMs: currentIatMs
          ? Math.round((now - currentIatMs) / 1000) + 's ago'
          : 'never',
        countdownActive: dialogCountdownActiveRef.current,
      });
      if (shouldOpen) {
        showDialog(expiration);
      }
    }
  }, [showDialog, activity, leader, scheduleRefresh, handleLogout]);

  // ============================================================================
  // CROSS-TAB MESSAGE HANDLING
  // ============================================================================

  const handleSessionMessage = useCallback(
    (data) => {
      switch (data.type) {
        case MESSAGE_TYPES.TOKEN_REFRESHED:
          if (data.tabId !== getTabId()) {
            console.log('[Auth0Session] 🔄 Token refreshed by another tab', {
              tokenChanged: data.tokenChanged,
              newExpiry: data.expiration
                ? Math.round((data.expiration - Date.now()) / 1000) + 's'
                : 'unknown',
            });

            // Always update expiration refs so all tabs know current token state
            if (data.expiration) {
              lastTokenExpMsRef.current = data.expiration;
            }

            // Sync IAT tracking — activity must come from real user interaction only
            if (data.tokenChanged && data.tokenIatMs) {
              currentTokenIatMsRef.current = data.tokenIatMs;
              activity.updateRefreshTimestamp(data.tokenIatMs);
            }

            scheduleRefresh();
            if (isDialogOpenRef.current) {
              dismissDialog();
            }
          }
          break;

        case MESSAGE_TYPES.SESSION_RESET:
          // User clicked Continue in another tab
          if (data.tabId !== getTabId()) {
            console.log(
              '[Auth0Session] 🔄 Session reset by another tab (user continued)'
            );

            // Sync the justContinued guard timestamp to prevent dialog reopening in this tab
            if (data.source === 'dialog' && data.continueTime) {
              justContinuedAtRef.current = data.continueTime;
            }

            // Update activity timestamp to match the other tab
            if (data.activityTs) {
              writeTimestamp(STORAGE_KEYS.ACTIVITY, data.activityTs);
            }

            // Close dialog if open
            if (isDialogOpenRef.current) {
              dismissDialog();
            }

            // CRITICAL: If we're the leader, we need to reschedule refresh.
            // The tab that clicked Continue might be a follower, in which case
            // its scheduleRefresh() returned early. The leader must ensure
            // a refresh is scheduled.
            if (leader.isLeader()) {
              scheduleRefresh();
            }
          }
          break;

        case MESSAGE_TYPES.REFRESH_REQUESTED:
          if (leader.isLeader() && data.tabId !== getTabId()) {
            console.log('[Auth0Session] 🔄 Follower requested refresh');
            performRefresh().catch(() => {
              /* terminal error — handleLogout already called */
            });
          }
          break;

        case MESSAGE_TYPES.DIALOG_SHOWN:
          if (data.tabId !== getTabId() && !isDialogOpenRef.current) {
            // Trust the broadcasting tab's decision to show the dialog.
            // Don't second-guess with local activity check — our tokenIatMs may be
            // stale if we missed a TOKEN_REFRESHED broadcast, causing us to
            // incorrectly think the user is active and ignore the dialog.
            console.log('[Auth0Session] 🔔 Showing dialog from cross-tab broadcast');
            showDialog(data.expiration);
          }
          break;

        case MESSAGE_TYPES.DIALOG_DISMISSED:
          if (data.tabId !== getTabId() && isDialogOpenRef.current) {
            console.log('[Auth0Session] 🔔 Dialog dismissed by another tab');
            dismissDialog();
          }
          break;

        case MESSAGE_TYPES.LOGOUT_REQUESTED:
          handleLogout(data.reason, { skipBroadcast: true });
          break;

        default:
          break;
      }
    },
    [
      leader,
      scheduleRefresh,
      performRefresh,
      showDialog,
      dismissDialog,
      handleLogout,
      activity,
    ]
  );

  // ============================================================================
  // INITIALIZATION
  // ============================================================================

  useEffect(() => {
    if (auth0.isLoading) return;
    if (!auth0.isAuthenticated) return;

    console.log('[Auth0Session] 🚀 Initializing session', {
      tabId: getTabId(),
      isLeader: leader.isLeader(),
      config: {
        warningThresholdS: WARNING_THRESHOLD_MS / 1000,
        sdkLeewayS: AUTH0_SDK_LEEWAY_MS / 1000,
        refreshBufferS: AUTH0_REFRESH_BUFFER_MS / 1000,
        storageDebounceS: STORAGE_KEY_DEBOUNCE_MS / 1000,
        storageDebounceRefreshingS: STORAGE_KEY_DEBOUNCE_REFRESHING_MS / 1000,
        graceS: JUST_CONTINUED_GRACE_MS / 1000,
      },
    });

    // Initialize token tracking refs from current token
    const initializeTokenRefs = async () => {
      try {
        // Use cache-only to avoid triggering SDK's internal refresh during init
        const token = await auth0Ref.current.getAccessTokenSilently({
          cacheMode: 'cache-only',
        });
        // v2: cache-only returns undefined on miss — skip init if no token
        if (!token) {
          console.warn(
            '[Auth0Session] No cached token during init — will populate on first refresh'
          );
          return;
        }
        const decoded = jwtDecode(token);
        if (decoded?.iat) {
          currentTokenIatMsRef.current = decoded.iat * 1000;
        }
        if (decoded?.exp) {
          lastTokenExpMsRef.current = decoded.exp * 1000;
        }
        lastTokenSigRef.current = token.slice(-20);
      } catch (error) {
        console.warn('[Auth0Session] Failed to initialize token refs:', error);
      }
    };
    initializeTokenRefs();

    activity.initializeFromToken();
    activity.initializeListeners(() => isDialogOpenRef.current);

    const unsubscribeSession = subscribe(CHANNEL_NAMES.SESSION, handleSessionMessage);

    leader.acquire(
      () => scheduleRefresh(),
      () => {
        if (refreshTimeoutRef.current) {
          clearTimeout(refreshTimeoutRef.current);
        }
      }
    );

    checkShouldShowDialog();
    dialogCheckIntervalRef.current = setInterval(
      checkShouldShowDialog,
      DIALOG_CHECK_INTERVAL_MS
    );

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        console.log('[Auth0Session] 👁️ Tab became visible', {
          isLeader: leader.isLeader(),
          hasRefreshTimeout: !!refreshTimeoutRef.current,
          isDialogOpen: isDialogOpenRef.current,
          dialogCountdownActive: dialogCountdownActiveRef.current,
        });

        // Check dialog state from localStorage (might have been opened by another tab)
        const dialogOpenTimestamp = readTimestamp(STORAGE_KEYS.DIALOG_OPEN, 0);
        if (dialogOpenTimestamp > 0 && !isDialogOpenRef.current) {
          checkShouldShowDialog();
        } else if (!isDialogOpenRef.current) {
          checkShouldShowDialog();
        }

        // Only reschedule if we're leader and don't already have a pending refresh
        if (leader.isLeader() && !refreshTimeoutRef.current) {
          scheduleRefresh();
        } else if (!leader.isLeader()) {
          // Leader may have died while we were hidden/sleeping — attempt promotion
          leader.tryClaimLeadership().then((promoted) => {
            if (promoted) {
              console.log('[Auth0Session] 👑 Promoted to leader on visibility change');
              scheduleRefresh();
            }
          });
        }
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    const handleStorageEvent = (event) => {
      // Handle storage cleared (logout from another tab)
      if (event.key === null) {
        handleLogout('storage_cleared');
        return;
      }

      // Handle Auth0 storage key WRITE (token updated by another tab's refresh).
      // This is the fallback sync mechanism when BroadcastChannel TOKEN_REFRESHED
      // messages are missed (e.g., due to browser throttling or timing gaps).
      // The Auth0 SDK writes new tokens to localStorage, so we can decode them
      // to keep our expiration and IAT refs in sync across tabs.
      if (event.key?.includes('@@auth0spajs@@') && event.newValue) {
        try {
          const parsed = JSON.parse(event.newValue);
          const token = parsed?.body?.access_token;
          if (token) {
            const decoded = jwtDecode(token);
            if (decoded?.exp && decoded?.iat) {
              const newExpMs = decoded.exp * 1000;
              const newIatMs = decoded.iat * 1000;
              // Only update if this is a newer token
              if (newExpMs > (lastTokenExpMsRef.current || 0)) {
                lastTokenExpMsRef.current = newExpMs;
                currentTokenIatMsRef.current = newIatMs;
                console.log('[Auth0Session] 📦 Synced token refs from storage update', {
                  newExpSec: Math.round((newExpMs - Date.now()) / 1000),
                });
              }
            }
          }
        } catch {
          // Ignore parse errors — not all @@auth0spajs@@ entries contain parseable tokens
        }
        // Don't return — fall through to removal check in case this is a rotation
        // where oldValue was removed and newValue was set on the same key
      }

      // Handle Auth0 storage key removal (logout via removeItem)
      // IMPORTANT: Don't react immediately. The removal could be from:
      //   1. Auth0 SDK cache rotation during a refresh triggered by another tab's
      //      getAccessTokenSilently() call (SDK removes old entry before writing new one)
      //   2. Our own handleLogout in another tab - BroadcastChannel handles this faster
      //   3. SDK clearing cache after a failed refresh token exchange
      // Debounce to let BroadcastChannel LOGOUT_REQUESTED arrive first (case 2),
      // and verify auth state before forcibly logging out.
      if (event.key?.includes('@@auth0spajs@@') && !event.newValue && event.oldValue) {
        if (storageKeyRemovedTimeoutRef.current) {
          clearTimeout(storageKeyRemovedTimeoutRef.current);
        }

        // Dynamic debounce: allow more time when a refresh is in progress,
        // SCHEDULED (pending timeout), or when user just clicked Continue.
        // The leader's scheduleRefresh calls cache-only which can trigger SDK
        // cache cleanup (removing the key from localStorage) right before
        // performRefresh runs. Without checking for a pending refresh, follower
        // tabs see the removal and log out before the leader can complete the
        // refresh.
        //
        // CRITICAL: When user clicks Continue in another tab, SESSION_RESET
        // syncs justContinuedAtRef BEFORE the leader's performRefresh removes
        // the cache key. A cross-tab refresh is almost certainly in flight,
        // so use the extended debounce to avoid false logout.
        const isRefreshing = isRefreshingRef.current;
        const hasScheduledRefresh = !!refreshTimeoutRef.current;
        const justContinuedRecently =
          justContinuedAtRef.current &&
          Date.now() - justContinuedAtRef.current < JUST_CONTINUED_GRACE_MS;
        const debounceMs =
          isRefreshing || hasScheduledRefresh || justContinuedRecently
            ? STORAGE_KEY_DEBOUNCE_REFRESHING_MS
            : STORAGE_KEY_DEBOUNCE_MS;

        console.log('[Auth0Session] 🗑️ @@auth0spajs@@ key removed', {
          key: event.key,
          debounceMs,
          reason: isRefreshing
            ? 'refresh-in-progress'
            : hasScheduledRefresh
              ? 'refresh-scheduled'
              : justContinuedRecently
                ? 'just-continued'
                : 'no-refresh-pending',
          isLeader: leader.isLeader(),
          tabId: getTabId(),
        });

        storageKeyRemovedTimeoutRef.current = setTimeout(async () => {
          storageKeyRemovedTimeoutRef.current = null;

          // Snapshot all @@auth0spajs@@ keys for forensics
          const auth0Keys = Object.keys(localStorage).filter((k) =>
            k.includes('@@auth0spajs@@')
          );
          console.log('[Auth0Session] 🗑️ Debounce fired, checking state', {
            auth0KeysInStorage: auth0Keys.length,
            isRefreshing: isRefreshingRef.current,
            hasScheduledRefresh: !!refreshTimeoutRef.current,
            isAuthenticated: !!auth0Ref.current?.isAuthenticated,
            isLeader: leader.isLeader(),
            eventKeyStillExists: event.key ? !!localStorage.getItem(event.key) : 'n/a',
          });

          // If already logged out via BroadcastChannel, skip
          if (!auth0Ref.current?.isAuthenticated) {
            console.log('[Auth0Session] @@auth0spajs@@ removed - already logged out');
            return;
          }

          // Fast check: was the key re-added to localStorage directly?
          // This handles the SDK's remove→set rotation without an SDK call.
          if (event.key && localStorage.getItem(event.key)) {
            console.log(
              '[Auth0Session] @@auth0spajs@@ key re-added to localStorage - ignoring'
            );
            return;
          }

          // SDK cache check as fallback
          // v2: cache-only returns undefined (not throw) on miss, so check explicitly
          try {
            const cachedToken = await auth0Ref.current.getAccessTokenSilently({
              cacheMode: 'cache-only',
            });
            if (cachedToken) {
              console.log(
                '[Auth0Session] @@auth0spajs@@ key removed but token still cached - ignoring'
              );
              return;
            }
          } catch {
            // Can't get cached token
          }

          // If a refresh is STILL in progress, or user just clicked Continue
          // (cross-tab refresh likely in flight), extend the wait once more.
          // The SDK may write the new key after our debounce if the network
          // round-trip was particularly slow.
          const stillRefreshing = isRefreshingRef.current;
          const stillInGrace =
            justContinuedAtRef.current &&
            Date.now() - justContinuedAtRef.current < JUST_CONTINUED_GRACE_MS;

          if (stillRefreshing || stillInGrace) {
            console.log(
              '[Auth0Session] @@auth0spajs@@ removed but cross-tab refresh likely - extending wait',
              {
                reason: stillRefreshing ? 'refresh-in-progress' : 'just-continued',
              }
            );
            storageKeyRemovedTimeoutRef.current = setTimeout(async () => {
              storageKeyRemovedTimeoutRef.current = null;
              if (!auth0Ref.current?.isAuthenticated) return;

              // Re-check localStorage and SDK cache
              if (event.key && localStorage.getItem(event.key)) return;
              // v2: cache-only returns undefined (not throw) on miss
              try {
                const cachedToken = await auth0Ref.current.getAccessTokenSilently({
                  cacheMode: 'cache-only',
                });
                if (cachedToken) return;
              } catch {
                // Still no token
              }
              console.log(
                '[Auth0Session] @@auth0spajs@@ removed after extended wait - logging out'
              );
              handleLogout('storage_key_removed');
            }, STORAGE_KEY_DEBOUNCE_REFRESHING_MS);
            return;
          }

          console.log(
            '[Auth0Session] @@auth0spajs@@ removed and no cached token - logging out'
          );
          handleLogout('storage_key_removed');
        }, debounceMs);
        return;
      }

      // NOTE: We intentionally do NOT sync on Auth0 storage changes here.
      // Cross-tab sync is handled by BroadcastChannel in handleSessionMessage.
      // The storage event fires AFTER our broadcast, and updating refresh timestamp
      // here would overwrite the already-synced timestamps and break isUserActive().
    };
    window.addEventListener('storage', handleStorageEvent);

    // DIAGNOSTIC: Track unexpected page unloads to debug the "page refresh mystery"
    // This logs state when the page is about to unload, helping identify if the
    // reload is caused by our code, Auth0 SDK, or something else.
    const handleBeforeUnload = () => {
      console.log('[Auth0Session] ⚠️ Page unloading (beforeunload)', {
        isDialogOpen: isDialogOpenRef.current,
        dialogCountdownActive: dialogCountdownActiveRef.current,
        countdownExpiration: countdownExpirationRef.current,
        isRefreshing: isRefreshingRef.current,
        hasRefreshTimeout: !!refreshTimeoutRef.current,
        timestamp: Date.now(),
      });
      // Note: We don't prevent the unload, just log for diagnostics
    };
    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      unsubscribeSession();
      leader.cleanup();
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('storage', handleStorageEvent);
      window.removeEventListener('beforeunload', handleBeforeUnload);

      if (refreshTimeoutRef.current) clearTimeout(refreshTimeoutRef.current);
      if (dialogCheckIntervalRef.current) clearInterval(dialogCheckIntervalRef.current);
      // NOTE: Do NOT clear countdownIntervalRef here. It is managed by
      // showDialog/dismissDialog/handleLogout. Clearing it on effect re-run
      // kills the dialog countdown while the dialog is still visible, causing
      // the frozen-dialog-with-negative-times bug.
      if (storageKeyRemovedTimeoutRef.current)
        clearTimeout(storageKeyRemovedTimeoutRef.current);
    };
  }, [
    auth0.isLoading,
    auth0.isAuthenticated,
    handleSessionMessage,
    scheduleRefresh,
    checkShouldShowDialog,
    leader,
    activity,
    handleLogout,
  ]);

  // ============================================================================
  // SETUP AXIOS TOKEN REFRESH HANDLER
  // ============================================================================

  useEffect(() => {
    if (!auth0Ref.current) return;

    const auth0Handler = async () => {
      const currentAuth0 = auth0Ref.current;

      // CRITICAL: Block API calls when countdown is active - user must respond to dialog
      // This is more specific than isDialogOpen because it only triggers once countdown starts
      if (dialogCountdownActiveRef.current) {
        throw new Error('Session countdown active - user action required');
      }

      if (isDialogOpenRef.current) {
        throw new Error('Session dialog is open - user action required');
      }

      if (!navigator.onLine) {
        throw new Error('Offline - cannot refresh token');
      }

      if (!currentAuth0?.isAuthenticated) {
        throw new Error('Not authenticated');
      }

      // CRITICAL: Use cache-only to prevent the Auth0 SDK from triggering its own
      // refresh (iframe or refresh token exchange) when the token has ≤60s remaining.
      // Uncontrolled SDK refreshes bypass our leader election, activity checks, and
      // dialog logic. If the RT exchange fails, the SDK clears @@auth0spajs@@ from
      // localStorage, which cascades to logout in ALL tabs via the storage event.
      // Our scheduleRefresh (leader-only, at T-55s) handles refresh timing correctly.
      try {
        const token = await currentAuth0.getAccessTokenSilently({
          cacheMode: 'cache-only',
        });
        // v2: cache-only returns undefined on miss — fall through to refresh logic
        if (!token) {
          throw new Error('Cache miss: cache-only returned undefined');
        }
        return token;
      } catch (cacheError) {
        // cache-only failed: token expired or missing from cache.
        // Delegate to the leader's controlled refresh.
        console.log(
          '[Auth0Session] Cache-only token read failed for API call:',
          cacheError.message
        );

        if (isTerminalError(cacheError)) {
          throw cacheError;
        }

        // As leader, attempt a controlled refresh
        if (leader.isLeader()) {
          console.log('[Auth0Session] Leader attempting controlled refresh for API call');
          const result = await performRefresh();
          if (result?.token) {
            return result.token;
          }
          throw cacheError;
        }

        // ── Follower recovery ──────────────────────────────────────────
        // The leader tab may have closed. Web Lock is auto-released on
        // tab close, so we can try to claim leadership immediately.
        console.log('[Auth0Session] Follower attempting recovery for API call');

        // Step 1: Try to claim the now-free Web Lock
        const promoted = await leader.tryClaimLeadership();
        if (promoted) {
          console.log('[Auth0Session] Follower promoted to leader, refreshing');
          const result = await performRefresh();
          if (result?.token) {
            return result.token;
          }
          throw cacheError;
        }

        // Step 2: Leader is still alive but hasn't refreshed yet.
        // Ask leader to refresh and poll for the cached token.
        broadcast(CHANNEL_NAMES.SESSION, {
          type: MESSAGE_TYPES.REFRESH_REQUESTED,
          tabId: getTabId(),
        });

        const deadline = Date.now() + FOLLOWER_WAIT_TIMEOUT_MS;
        while (Date.now() < deadline) {
          await new Promise((r) => setTimeout(r, 500));

          // Check if cached token appeared (leader may have refreshed)
          try {
            const freshToken = await currentAuth0.getAccessTokenSilently({
              cacheMode: 'cache-only',
            });
            // v2: cache-only returns undefined on miss — keep polling
            if (freshToken) return freshToken;
          } catch {
            // Not available yet
          }

          // Re-check promotion (leader may have closed during our wait)
          if (await leader.tryClaimLeadership()) {
            const result = await performRefresh();
            if (result?.token) return result.token;
            break;
          }
        }

        // Exhausted retries — propagate so axios 401 handler can redirect
        throw cacheError;
      }
    };

    setTokenRefreshHandlers(auth0Handler, null, 'auth0');

    return () => {
      setTokenRefreshHandlers(null, null, null);
    };
  }, [performRefresh, leader]);

  const formatCountdown = useCallback(() => {
    if (countdown <= 0) return '0:00';
    const minutes = Math.floor(countdown / 60);
    const seconds = countdown % 60;
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  }, [countdown]);

  return {
    // Dialog state
    isDialogOpen,
    expirationTimestamp: dialogExpirationTimestamp,
    countdown,
    countdownFormatted: formatCountdown(),

    // Actions
    continueSession: handleContinue,
    logout: handleLogout,
    dismissDialog,

    // Internal state (for debugging)
    isLeader: leader.isLeader(),

    // Activity helpers
    signalActivity: (source) => activity.signalActivity(source),
    isUserActive: activity.isUserActive,
    resetActivityTimestamp: activity.resetTimestamps,
  };
};

export default useAuth0Session;
